import { getMessagesBatch } from "../../../api/messages";
import type { InboxV2Account, InboxV2ConversationMessage } from "../../types";
import {
  mapMessageToInboxV2Message,
  shouldStoreInboxV2Message,
} from "../mapper";
import type { InboxV2RawMessage } from "./types";

const MESSAGE_BATCH_WINDOW_MS = 10;
const MESSAGE_CACHE_TTL_MS = 5_000;

export interface InboxV2ConversationMessagesPage {
  messages: InboxV2ConversationMessage[];
  nextToken?: string;
}

interface MessageFetchInput {
  account?: InboxV2Account;
  conversationId: string;
  nextToken?: string;
}

interface PendingMessageFetch {
  input: MessageFetchInput;
  key: string;
  reject(error: unknown): void;
  resolve(page: InboxV2ConversationMessagesPage): void;
}

class InboxV2MessageFetchManager {
  private cache = new Map<
    string,
    { fetchedAt: number; page: InboxV2ConversationMessagesPage }
  >();
  private inFlight = new Map<
    string,
    Promise<InboxV2ConversationMessagesPage>
  >();
  private pending: PendingMessageFetch[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;

  fetch(input: MessageFetchInput) {
    const key = this.createKey(input);
    const cached = this.cache.get(key);

    if (
      !input.nextToken &&
      cached &&
      Date.now() - cached.fetchedAt < MESSAGE_CACHE_TTL_MS
    ) {
      return Promise.resolve(cached.page);
    }

    const inFlight = this.inFlight.get(key);

    if (inFlight) {
      return inFlight;
    }

    const promise = new Promise<InboxV2ConversationMessagesPage>(
      (resolve, reject) => {
        this.pending.push({ input, key, reject, resolve });
        this.scheduleFlush();
      }
    );

    this.inFlight.set(key, promise);

    return promise.finally(() => {
      this.inFlight.delete(key);
    });
  }

  invalidateConversation(conversationId: string) {
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${conversationId}:`)) {
        this.cache.delete(key);
      }
    }
  }

  private scheduleFlush() {
    if (this.timer) {
      return;
    }

    this.timer = setTimeout(() => this.flush(), MESSAGE_BATCH_WINDOW_MS);
  }

  private async flush() {
    const batch = this.pending;

    this.pending = [];
    this.timer = undefined;

    try {
      const { data } = await getMessagesBatch({
        requests: batch.map(({ input }) => ({
          conversationId: input.conversationId,
          next: input.nextToken,
        })),
      });

      batch.forEach((pending, index) => {
        const response = data[index];

        if (!response) {
          pending.reject(new Error("Message batch response was incomplete"));
          return;
        }

        const page = {
          messages: [...(response.items ?? [])]
            .reverse()
            .filter((message) =>
              shouldStoreInboxV2Message(message as InboxV2RawMessage)
            )
            .map((message) => ({
              ...mapMessageToInboxV2Message(
                {
                  ...(message as InboxV2RawMessage),
                  reactions: message.reactions ?? [],
                },
                {
                  account: pending.input.account,
                  conversationAccountId: pending.input.account?.id,
                }
              ),
              conversationId: pending.input.conversationId,
            })),
          nextToken: response.next ?? undefined,
        };

        if (!pending.input.nextToken) {
          this.cache.set(pending.key, { fetchedAt: Date.now(), page });
        }

        pending.resolve(page);
      });
    } catch (error) {
      batch.forEach((pending) => pending.reject(error));
    }
  }

  private createKey(input: MessageFetchInput) {
    return `${input.conversationId}:${input.nextToken ?? "initial"}`;
  }
}

export const inboxV2MessageFetchManager = new InboxV2MessageFetchManager();
